import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { useConfirm } from "@/components/useConfirm";
import { toast } from "sonner";
import { LearnerActivityDialog } from "@/components/LearnerActivityDialog";
import { Plus, Upload, Trash2, Users, Mail, Building2, Download, Activity, Lock, Search, FileSpreadsheet } from "lucide-react";

export default function LearnersPage() {
  const [learners, setLearners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", department: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { confirm, ConfirmDialog } = useConfirm();
  const [activityLearner, setActivityLearner] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = learners.filter((l) =>
    (l.name || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  const openActivity = (l) => { setActivityLearner(l); setActivityOpen(true); };

  const load = async () => {
    const { data } = await api.get("/learners");
    setLearners(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.email) return toast.error("Name & email required");
    setSaving(true);
    try {
      await api.post("/learners", form);
      toast.success("Learner added");
      setOpen(false);
      setForm({ name: "", email: "", department: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (l) => {
    const ok = await confirm({
      title: `Remove ${l.name}?`,
      description: "They will be removed from the roster. Existing score records are preserved.",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    await api.delete(`/learners/${l.id}`);
    load();
    toast.success("Removed");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/learners/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} learners`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = () => {
    const csv = "name,email,department\nAria Sharma,aria@example.com,Sales\nLeo Martinez,leo@example.com,Engineering\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "learners-template.csv";
    a.click();
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN") : "");
    const header = ["Name", "Email", "Audience", "Department", "Last Login", "Signed Up"];
    const lines = learners.map((l) => [
      esc(l.name), esc(l.email),
      esc((l.audience || "employee") === "dealer" ? "Dealer" : "Employee"),
      esc(l.department || ""), esc(fmt(l.last_login_at)), esc(fmt(l.added_at)),
    ].join(","));
    const blob = new Blob(["\ufeff" + [header.map(esc).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `univicoustic-learners-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success(`Exported ${learners.length} learners`);
  };

  return (
    <AdminLayout>
      <div className="px-10 py-10 max-w-6xl">
        <header className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Audience</div>
            <h1 className="text-4xl font-display font-bold text-slate-900 tracking-tight">Learners</h1>
            <p className="text-slate-500 mt-2">Add team members manually or bulk-import from Excel.</p>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onUpload} data-testid="learner-excel-input" />
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
              <Download size={14} className="mr-1.5" /> Template
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={learners.length === 0} data-testid="export-learners-button">
              <FileSpreadsheet size={14} className="mr-1.5" /> Export CSV
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="import-excel-button">
              <Upload size={14} className="mr-1.5" /> {uploading ? "Importing..." : "Import CSV/Excel"}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-learner-button" className="bg-blue-900 hover:bg-blue-800 text-white">
                  <Plus size={14} className="mr-1.5" /> Add Learner
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add learner</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input data-testid="learner-name-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="learner-email-input" type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Department (optional)</Label>
                    <Input data-testid="learner-dept-input" value={form.department} onChange={(e)=>setForm({...form, department: e.target.value})} className="mt-1.5" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button data-testid="save-learner-button" onClick={add} disabled={saving} className="bg-blue-900 hover:bg-blue-800 text-white">
                    {saving ? "Saving..." : "Add"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        <Card className="border-slate-200 bg-slate-50 p-4 mb-6">
          <div className="text-xs text-slate-700">
            <strong>Self-serve accounts:</strong> Learners sign up with email &amp; password and appear here instantly. Click any learner to see their full account activity — sign-ins, quiz attempts and simulator sessions. Admins can also pre-add learners or import in bulk via CSV/Excel.
          </div>
        </Card>

        {learners.length > 0 && (
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="learner-search-input"
                placeholder="Search learners by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            {search.trim() && (
              <span className="text-sm text-slate-500" data-testid="learner-search-count">
                Showing {filtered.length} of {learners.length}
              </span>
            )}
          </div>
        )}

        {learners.length === 0 ? (
          <Card className="p-16 text-center border-dashed">
            <Users size={40} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3} />
            <div className="text-slate-700 font-medium">No learners yet</div>
            <div className="text-sm text-slate-500 mt-1">Add learners individually or upload an Excel sheet with name, email, department columns.</div>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center border-dashed" data-testid="learner-search-empty">
            <Search size={32} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3} />
            <div className="text-slate-700 font-medium">No learners match &ldquo;{search.trim()}&rdquo;</div>
            <div className="text-sm text-slate-500 mt-1">Try a different name.</div>
          </Card>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Department</th>
                  <th className="px-5 py-3 font-semibold">Added</th>
                  <th className="px-5 py-3 font-semibold w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" data-testid={`learner-row-${l.id}`} onClick={() => openActivity(l)}>
                    <td className="px-5 py-3 text-sm font-medium text-slate-900">
                      <span className="inline-flex items-center gap-1.5">
                        {l.name}
                        {l.locked && <Lock size={12} className="text-amber-600" title="Account locked" data-testid={`locked-icon-${l.id}`} />}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-600 inline-flex items-center gap-1.5"><Mail size={12}/> {l.email}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{l.department ? <span className="inline-flex items-center gap-1"><Building2 size={12}/> {l.department}</span> : "—"}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{new Date(l.added_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <button onClick={(e) => { e.stopPropagation(); openActivity(l); }} className="text-slate-400 hover:text-blue-700" title="View activity" data-testid={`view-activity-${l.id}`}>
                          <Activity size={14} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); remove(l); }} className="text-slate-400 hover:text-red-600" data-testid={`delete-learner-${l.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
      <ConfirmDialog />
      <LearnerActivityDialog learner={activityLearner} open={activityOpen} onOpenChange={setActivityOpen} />
    </AdminLayout>
  );
}
